import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import SearchOverlay from './SearchOverlay';
import { useCart } from '../../context/CartContext';
import { getCategories } from '../../api/apiServices';
import logo from '../../asset/MAKKLI.png';
import { slugify } from '../../utils/slugify';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const { cartCount, setIsCartOpen } = useCart();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  const location = useLocation();
  const isHomePage = location.pathname === '/';

  /* ---------------- SCROLL EFFECT ---------------- */
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  /* ---------------- LOAD DATA ---------------- */
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const res = await getCategories();
        setCategories(res.data || []);
      } catch (err) {
        console.error("Failed to load shop data:", err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  /* ---------------- LOCK BODY SCROLL ---------------- */
  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? 'hidden' : 'auto';
  }, [isMobileMenuOpen]);

  const shouldShowWhiteBg = isScrolled || !isHomePage;

  return (
    <>
      {/* NAVBAR */}
      <nav className={`fixed top-0 w-full z-40 transition-all duration-300 ${shouldShowWhiteBg ? 'bg-cream-200 shadow-md' : 'bg-transparent'}`}>
        <div className="w-full max-w-[1200px] mx-auto px-4 flex items-center justify-between py-4">
          
          {/* LOGO */}
          <Link to="/" className="flex items-center">
            <img
              src={logo}
              alt="MAKKLI"
              className="h-16 md:h-18 transition-all duration-300"
            />
          </Link>

          {/* DESKTOP MENU */}
          <div className="hidden md:flex gap-6 lg:gap-8 font-medium items-center">
            {categories.slice(0, 6).map((category) => (
              <Link
                key={category._id}
                to={`/products/${slugify(category.name)}`}
                className={`transition-all duration-200 hover:text-lilac-400 whitespace-nowrap text-sm font-['Poppins'] ${shouldShowWhiteBg ? 'text-gray-700' : 'text-white'}`}
              >
                {category.name}
              </Link>
            ))}
            <Link
              to="/products/all"
              className="text-lilac-400 font-semibold text-sm hover:opacity-80 transition-opacity"
            >
              All categories
            </Link>
          </div>

          {/* RIGHT ICONS */}
          <div className="flex items-center gap-3">
            
            {/* SEARCH */}
            <button
              onClick={() => setIsSearchOpen(true)}
              className={`w-10 h-10 flex items-center justify-center rounded-full transition-colors ${shouldShowWhiteBg ? 'text-gray-700 hover:text-lilac-400' : 'text-white hover:text-lilac-400'}`}
            >
              <i className="ri-search-line text-xl"></i>
            </button>

            {/* CART */}
            <button 
              onClick={() => setIsCartOpen(true)}
              className="bg-lilac-400 flex justify-center items-center w-10 h-10 rounded-full relative hover:bg-lilac-300 transition-colors"
            >
              <i className="ri-shopping-bag-line text-white text-lg"></i>
              <div className="w-5 h-5 bg-white absolute flex justify-center items-center text-lilac-400 -right-1 -top-1 rounded-full shadow-sm">
                <p className="text-[10px] font-bold">{cartCount}</p>
              </div>
            </button>

            {/* MOBILE MENU BUTTON */}
            <div className="md:hidden">
              <button
                onClick={() => setIsMobileMenuOpen(true)}
                className={`w-10 h-10 flex items-center justify-center rounded-full ${shouldShowWhiteBg ? 'text-gray-700' : 'text-white'}`}
              >
                <i className="ri-menu-line text-xl"></i>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* MOBILE DRAWER */}
      <div className={`fixed inset-0 z-50 ${isMobileMenuOpen ? 'visible' : 'invisible'}`}>
        
        {/* BACKDROP */}
        <div
          onClick={() => setIsMobileMenuOpen(false)}
          className={`absolute inset-0 bg-black/40 transition-opacity duration-300 ${isMobileMenuOpen ? 'opacity-100' : 'opacity-0'}`}
        />

        {/* DRAWER PANEL */}
        <div className={`absolute top-0 right-0 h-full w-[75%] max-w-[320px] bg-cream-100 shadow-xl transform transition-transform duration-300 ${isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'}`}>

          {/* HEADER */}
          <div className="flex items-center justify-between px-5 py-4 border-b">
            <h2 className="font-semibold text-lg">Menu</h2>
            <button onClick={() => setIsMobileMenuOpen(false)}>
              <i className="ri-close-line text-2xl"></i>
            </button>
          </div>

          {/* MENU ITEMS */}
          <div className="flex flex-col py-3">
            {categories.map((category) => (
              <Link
                key={category._id}
                to={`/products/${slugify(category.name)}`}
                onClick={() => setIsMobileMenuOpen(false)}
                className="px-5 py-3 text-gray-700 hover:bg-gray-100 text-sm font-['Poppins']"
              >
                {category.name}
              </Link>
            ))}

            <Link
              to="/products/all"
              onClick={() => setIsMobileMenuOpen(false)}
              className="px-5 py-3 text-lilac-400 font-semibold text-sm"
            >
              All Categories
            </Link>
          </div>
        </div>
      </div>

      {/* SEARCH OVERLAY */}
      <SearchOverlay 
        isOpen={isSearchOpen} 
        onClose={() => setIsSearchOpen(false)} 
      />
    </>
  );
}